import Div from "@jumbo/shared/Div/Div";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";

/**
 * Server-side table wrapper specifically for Matters listing.
 * NOTE: This is intentionally separate from the common DocketMuiTableWrapper to avoid changing shared behaviour.
 */
const MattersMuiTableWrapper = ({
    columns,
    tableClasses,
    tablePaginationClasses,
    hasCheckboxesColumn,
    selectedRows,
    setSelectedRows,
    disableCheckboxes = false,
    data,
    isFetching,
    page,
    rowsPerPage,
    totalCount,
    sortedColumn,
    sortDirection,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    rowsSelection,
    setRowsSelection,
    tableContainerStyle = {
        maxHeight: "70vh",
        overflowY: "auto"
    },
    ...props
}) => {
    const handleSelectAllRows = (event) => {
        if (event.target.checked) {
            setSelectedRows(prev => {
                const newRows = data.filter(row => !prev.some(r => r.id === row.id));
                return [...prev, ...newRows];
            });
            setRowsSelection(data);
        } else {
            const currentPageIds = new Set(data.map(row => row.id));
            setSelectedRows(prev => prev.filter(row => !currentPageIds.has(row.id)));
            setRowsSelection([]);
        }
    };

    const handleRowSelect = (event, row) => {
        const selected = event.target.checked;
        if (selected) {
            setSelectedRows(prev => [...prev, row]);
            setRowsSelection(prev => [...prev, row]);
        } else {
            setSelectedRows(prev => prev.filter(r => r.id !== row.id));
            setRowsSelection(prev => prev.filter(r => r.id !== row.id));
        }
    };

    const isAllCurrentPageSelected =
        Array.isArray(data) &&
        data.length > 0 &&
        data.every(row => rowsSelection.some(r => r.id === row.id));

    const RenderValue = ({ value, renderMethod = undefined, row }) => {
        if (renderMethod === undefined) {
            return <>{value}</>;
        }
        return renderMethod(value !== undefined ? value : null, row);
    };

    return (
        <div className="customize-mui-table-wrapper">
            <TableContainer component={Paper} sx={tableContainerStyle}>
                <Table
                    className={`customize-mui-table ${tableClasses ? tableClasses : ""}`}
                    stickyHeader
                    size="small"
                >
                    {props?.colgroup && (
                        <colgroup>
                            {(hasCheckboxesColumn === undefined ||
                                hasCheckboxesColumn === true) && (
                                <col style={{ width: "auto" }} />
                            )}
                            {props?.colgroup?.map((item, index) => (
                                <col key={index} style={item} />
                            ))}
                        </colgroup>
                    )}
                    <TableHead>
                        <TableRow>
                            {(hasCheckboxesColumn === undefined ||
                                hasCheckboxesColumn === true) &&
                                Array.isArray(data) &&
                                data.length > 0 && (
                                    <TableCell
                                        sx={{ cursor: "pointer", pr: "30px !important" }}
                                        padding="checkbox"
                                    >
                                        <Checkbox
                                            checked={isAllCurrentPageSelected}
                                            onChange={handleSelectAllRows}
                                            disabled={disableCheckboxes}
                                            sx={{
                                                "& input": {
                                                    zIndex: "999"
                                                },
                                                "& svg": {
                                                    zIndex: "99"
                                                }
                                            }}
                                        />
                                    </TableCell>
                                )}
                            {columns.map((column) => (
                                <TableCell
                                    key={column.field}
                                    style={{ cursor: "pointer", ...(column?.style || {}) }}
                                    sx={{ p: 0.8 }}
                                >
                                    {column.sort === undefined || column.sort === true ? (
                                        <TableSortLabel
                                            onClick={() => onSortChange(column.field)}
                                            active={sortedColumn === column.field}
                                            sx={{
                                                "&.Mui-active svg": {
                                                    color: "#fff !important"
                                                }
                                            }}
                                            direction={
                                                sortedColumn === column.field ? sortDirection : "asc"
                                            }
                                        >
                                            {column.header}
                                        </TableSortLabel>
                                    ) : (
                                        column.header
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isFetching ? (
                            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row) => (
                                <TableRow key={row}>
                                    {(hasCheckboxesColumn === undefined ||
                                        hasCheckboxesColumn === true) && (
                                        <TableCell padding="checkbox">
                                            <Skeleton animation={"wave"} variant="text" height={19} />
                                        </TableCell>
                                    )}
                                    {columns.map((column) => (
                                        <TableCell sx={{ p: 0.8 }} key={column.field}>
                                            <Skeleton animation={"wave"} variant="text" height={19} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : Array.isArray(data) && data.length > 0 ? (
                            data.map((row) => (
                                <TableRow
                                    key={row.id}
                                    hover
                                    selected={selectedRows.some((r) => r.id === row.id)}
                                >
                                    {(hasCheckboxesColumn === undefined ||
                                        hasCheckboxesColumn === true) && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedRows.some((r) => r.id === row.id)}
                                                onChange={(event) => handleRowSelect(event, row)}
                                                disabled={disableCheckboxes}
                                            />
                                        </TableCell>
                                    )}
                                    {columns.map((column) => (
                                        <TableCell sx={{ p: 0.8 }} key={column.field}>
                                            <RenderValue
                                                value={row[column.field]}
                                                renderMethod={column.render}
                                                row={row}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={
                                        columns?.length +
                                        (hasCheckboxesColumn === undefined ||
                                        hasCheckboxesColumn === true
                                            ? 1
                                            : 0)
                                    }
                                    align="center"
                                >
                                    <Div
                                        sx={{
                                            p: "100px 10px",
                                            backgroundColor: "#EFEFEF",
                                            "& h6": {
                                                fontSize: "20px",
                                                color: "#000"
                                            }
                                        }}
                                    >
                                        <h6 className="mb-0">No Data Found</h6>
                                    </Div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[50, 75, 100]}
                component="div"
                count={totalCount || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                className={`table-pagination ${tablePaginationClasses ? tablePaginationClasses : ""}`}
            />
        </div>
    );
};

export default MattersMuiTableWrapper;